import { Injectable } from '@angular/core';
import { AlertHandlerService } from '@finxone-platform/shared/services';
import {
  AccountInfoType,
  CurrentAccountInfoType,
  FileDocumentResponse,
  PaginatedResponse,
  PaginationMeta,
  PaymentProvider,
  SpendingBudget,
  ViewType,
  accountServicePaymentRequest,
} from '@finxone-platform/shared/sys-config-types';
import { Action, Selector, State, StateContext, createSelector } from '@ngxs/store';
import { Observable, catchError, tap, throwError } from 'rxjs';
import {
  ClearAccountCache,
  CreateCurrencyAccount,
  CreateCurrencyAccountSuccess,
  GetAccount,
  GetCurrentAccountDocumentDetails,
  SetAccountState,
  SetLoadingStatus,
  UpdateAccountName,
  UpdateAccountSpendingBudget,
  UpdateCurrentAccountId,
  UpdateCurrentMultiCurrencyCode,
  UpdatePaymentGatewayConfig,
} from '../actions/account.action';
import { AddProgressBarStack, RemoveProgressBarStack } from '../actions/progress-bar.action';
import { AccountService } from '../services/account-service/account-service.service';
import { UpdateAccountResponse } from '../services/account-service/account.type';
import { DocumentService } from '../services/document-service/document.service';
import { ApiActions, UpdateApiIsLoadingAction } from './api-loading.state';

export interface AccountStateModel {
  accounts: AccountInfoType[];
  accountActivePage: number;
  accountPageLimit: number;
  accountTotalPages: number;
  totalItems: number;
  currentAccountId: string;
  // For multi-currency accounts, this holds the currently selected currency code
  currentMultiCurrencyCode?: string;
  paymentGatewayConfig: PaymentGatewayConfig;
  isLoading: boolean;
  documentDetails?: FileDocumentResponse[];
  pagination: PaginationMeta;
}

export interface PaymentGatewayConfig {
  payment: PaymentProvider;
  sandboxMode?: string;
  checkPayeeEnabled: boolean;
  bankProvider: string;
  supportedCurrency: string[];
  paymentRequest: accountServicePaymentRequest;
}

@State<AccountStateModel>({
  name: 'account',
  defaults: {
    accounts: [],
    currentAccountId: '',
    accountPageLimit: 10,
    accountActivePage: 0,
    accountTotalPages: 0,
    totalItems: 0,
    isLoading: false,
    paymentGatewayConfig: {
      payment: PaymentProvider.MODULR,
      sandboxMode: '',
      supportedCurrency: [],
      bankProvider: '',
      checkPayeeEnabled: false,
      paymentRequest: {
        enabled: false,
        paymentApproverRoles: [],
        paymentRequestorRoles: [],
        paymentInitiatorRoles: [],
        paymentReaderRoles: [],
      },
    },
    documentDetails: [],
    pagination: {} as PaginationMeta,
  },
})
@Injectable()
export class AccountState {
  constructor(
    private accountService: AccountService,
    private alertHandlerService: AlertHandlerService,
    private documentService: DocumentService,
  ) {}

  @Selector()
  static getAccounts(state: AccountStateModel) {
    return state;
  }

  @Selector()
  static getCurrentAccountId(state: AccountStateModel) {
    return state.currentAccountId;
  }

  @Selector()
  static getCurrentMultiCurrencyCode(state: AccountStateModel) {
    return state.currentMultiCurrencyCode;
  }

  @Selector()
  static getCurrentAccount(state: AccountStateModel) {
    const account = state.accounts?.find((account) => account.accountId === state.currentAccountId);
    if (!account) {
      return undefined;
    } else {
      return { ...account, currentMultiCurrencyCode: state.currentMultiCurrencyCode };
    }
  }

  static getCurrentAccountDetails(currentAccountId: string) {
    return createSelector([AccountState], (state: AccountStateModel) => {
      return state.accounts?.find((account) => account.accountId === currentAccountId);
    });
  }

  @Selector()
  static getPaymentGatewayConfig(state: AccountStateModel) {
    return state.paymentGatewayConfig;
  }

  @Selector()
  static getLoadingStatus(state: AccountStateModel) {
    return state.isLoading;
  }

  @Action(SetLoadingStatus)
  setLoadingStatus(ctx: StateContext<AccountStateModel>, action: SetLoadingStatus) {
    try {
      ctx.patchState({
        ...ctx.getState(),
        isLoading: action.isLoading,
      });
      return true;
    } catch (err) {
      return throwError(() => err);
    }
  }

  @Action(SetAccountState)
  setAccountState(ctx: StateContext<AccountStateModel>, action: SetAccountState) {
    ctx.setState(action.state);
  }

  @Action(UpdatePaymentGatewayConfig)
  updatePaymentGatewayConfig(ctx: StateContext<AccountStateModel>, action: UpdatePaymentGatewayConfig) {
    ctx.patchState({
      ...ctx.getState(),
      paymentGatewayConfig: action.payload,
    });
  }

  @Action(ClearAccountCache)
  clearAccountCache(ctx: StateContext<AccountStateModel>) {
    ctx.patchState({
      accounts: [],
      currentAccountId: '',
      accountPageLimit: 10,
      accountActivePage: 0,
      accountTotalPages: 0,
      totalItems: 0,
      isLoading: false,
    });
  }

  @Action(GetAccount)
  fetchAccount(ctx: StateContext<AccountStateModel>, action: GetAccount) {
    ctx.dispatch(new AddProgressBarStack({ uniqueId: 'GetAccount' }));
    ctx.dispatch(new UpdateApiIsLoadingAction(ApiActions.getAccounts, true));

    let params = {
      page: action.page,
      size: action.limit,
      status: action.status,
    };
    if (action.searchParams) {
      params = { ...params, ...action.searchParams };
    }

    return this.accountService.getAccounts(params).pipe(
      tap((accountsResult: PaginatedResponse<AccountInfoType>) => {
        const prevData: AccountInfoType[] = ctx.getState().accounts;
        let accountPrevResult;

        if (action.viewType === ViewType.TABLE) {
          accountPrevResult = accountsResult.result;
        } else {
          if (action.page === 1) {
            accountPrevResult = accountsResult.result;
          } else {
            accountPrevResult = prevData.concat(accountsResult.result);
          }
        }

        ctx.patchState({
          accounts: accountPrevResult,
          accountActivePage: accountsResult.page,
          accountPageLimit: accountsResult.limit,
          accountTotalPages: accountsResult.totalPages,
          totalItems: accountsResult.totalItems,
          isLoading: false,
          pagination: {
            page: accountsResult.page,
            totalPages: accountsResult.totalPages ?? 1,
            totalItems: accountsResult.totalItems ?? 0,
            pageSize: accountsResult.limit,
          },
        });

        const currentId = ctx.getState().currentAccountId;
        const accountExists = accountsResult?.result?.some((account) => account.accountId === currentId);

        if (!currentId || (currentId && !accountExists)) {
          ctx.patchState({
            currentAccountId: accountsResult?.result?.[0]?.accountId,
          });
        }

        ctx.dispatch(new UpdateApiIsLoadingAction(ApiActions.getAccounts, false));
        ctx.dispatch(new RemoveProgressBarStack({ uniqueId: 'GetAccount' }));

        const finalAccountId = ctx.getState().currentAccountId;
        const finalAccount = ctx.getState().accounts.find((acc) => acc.accountId === finalAccountId);
        if (finalAccountId && finalAccount?.accountType === 'current') {
          ctx.dispatch(new UpdateAccountSpendingBudget(finalAccountId));
        }
      }),
      catchError<unknown, Observable<boolean>>((_err) => {
        ctx.dispatch(new UpdateApiIsLoadingAction(ApiActions.getAccounts, false));
        ctx.dispatch(new RemoveProgressBarStack({ uniqueId: 'GetAccount' }));
        throw _err;
      }),
    );
  }

  @Action(CreateCurrencyAccount)
  createCurrencyAccount(ctx: StateContext<AccountStateModel>, action: CreateCurrencyAccount) {
    ctx.dispatch(new AddProgressBarStack({ uniqueId: 'CreateCurrencyAccount' }));
    const payloadData = {
      currency: action.currency,
    };
    return this.accountService.addCurrencyAccount(payloadData).pipe(
      tap((response: AccountInfoType) => {
        const accounts: AccountInfoType[] = ctx.getState().accounts;
        accounts.push(response);
        this.alertHandlerService.showAlertFn('success', 'Currency Account added successfully');

        ctx.patchState({
          ...ctx.getState(),
          accounts: accounts,
        });

        ctx.dispatch(new RemoveProgressBarStack({ uniqueId: 'CreateCurrencyAccount' }));
        ctx.dispatch(new CreateCurrencyAccountSuccess());
      }),
      catchError<unknown, Observable<boolean>>((_err) => {
        ctx.dispatch(new RemoveProgressBarStack({ uniqueId: 'CreateCurrencyAccount' }));
        throw _err;
      }),
    );
  }

  @Action(UpdateCurrentAccountId)
  UpdateCurrentAccountId(ctx: StateContext<AccountStateModel>, action: UpdateCurrentAccountId) {
    const newAccount = ctx.getState().accounts.find((acc) => acc.accountId === action.currentAccountId);

    if (!newAccount) {
      console.error(`Account with ID ${action.currentAccountId} not found.`);
      return;
    }

    ctx.patchState({
      ...ctx.getState(),
      currentAccountId: action.currentAccountId,
      currentMultiCurrencyCode:
        newAccount.accountType === 'multi-currency' &&
        action.currentAccountId !== ctx.getState().currentAccountId
          ? newAccount.currency?.[0].currency
          : ctx.getState().currentMultiCurrencyCode,
    });
  }

  @Action(UpdateCurrentMultiCurrencyCode)
  updateCurrentMultiCurrencyCode(
    ctx: StateContext<AccountStateModel>,
    action: UpdateCurrentMultiCurrencyCode,
  ) {
    ctx.patchState({
      ...ctx.getState(),
      currentMultiCurrencyCode: action.currencyCode ?? ctx.getState().currentMultiCurrencyCode,
    });
  }

  @Action(UpdateAccountName)
  updateAccountName(ctx: StateContext<AccountStateModel>, action: UpdateAccountName) {
    ctx.dispatch(new AddProgressBarStack({ uniqueId: 'UpdateAccountName' }));

    return this.accountService.updateAccount(action.accountId, { name: action.name }).pipe(
      tap((response: UpdateAccountResponse) => {
        if (response) ctx.dispatch(new RemoveProgressBarStack({ uniqueId: 'UpdateAccountName' }));
      }),
      catchError<unknown, Observable<boolean>>((_err) => {
        ctx.dispatch(new RemoveProgressBarStack({ uniqueId: 'UpdateAccountName' }));
        throw _err;
      }),
    );
  }

  @Action(UpdateAccountSpendingBudget)
  updateAccountSpendingBudget(ctx: StateContext<AccountStateModel>, action: UpdateAccountSpendingBudget) {
    return this.accountService.getSpendingBudgetStatus(action.accountId).pipe(
      tap((budgetStatus) => {
        const accounts = ctx.getState().accounts;
        const updatedAccounts = accounts.map((account) => {
          if (account.accountId === action.accountId) {
            const spendingBudget: SpendingBudget = budgetStatus.isActive
              ? {
                  isActive: true,
                  limit: budgetStatus.limit,
                  spent: budgetStatus.spent,
                  remaining: budgetStatus.remaining,
                  resetDay: budgetStatus.resetDay,
                  periodStart: String(budgetStatus.periodStart),
                  periodEnd: String(budgetStatus.periodEnd),
                }
              : {
                  isActive: false,
                  limit: null,
                  spent: null,
                  remaining: null,
                  resetDay: null,
                  periodStart: null,
                  periodEnd: null,
                };
            return { ...(account as CurrentAccountInfoType), spendingBudget };
          }
          return account;
        });
        ctx.patchState({ accounts: updatedAccounts });
      }),
      catchError(() => {
        // Don't propagate — spending budget failure should not break other flows
        return [];
      }),
    );
  }

  @Action(GetCurrentAccountDocumentDetails)
  getCurrentAccountDocumentDetails(
    ctx: StateContext<AccountStateModel>,
    action: GetCurrentAccountDocumentDetails,
  ) {
    ctx.dispatch(new UpdateApiIsLoadingAction(ApiActions.documentsList, true));
    return this.documentService
      .getDocumentFiles(action.documentContext, action.accountId, action.documentCategory)
      .pipe(
        tap((response) => {
          ctx.patchState({
            ...ctx.getState(),
            documentDetails: response,
          });
          ctx.dispatch(new UpdateApiIsLoadingAction(ApiActions.documentsList, false));
        }),
        catchError((_err) => {
          ctx.dispatch(new UpdateApiIsLoadingAction(ApiActions.documentsList, false));
          throw _err;
        }),
      );
  }
}
